import {
  getStorage,
  ref,
  listAll,
  getDownloadURL,
  uploadBytes,
} from "firebase/storage";
import {
  doc,
  collection,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { db, storage } from "../../../firebase";

/**
 * Copies all files from users/{userId}/folders/{folderId} to admin/{userId}/folders/{folderId} in Firebase Storage.
 * @param {string} userId
 * @param {string} folderId
 */
export async function copyFolderFiles(userId, folderId, adminId) {
  const sourceFolderRef = ref(storage, `users/${userId}/folders/${folderId}`);
  const targetFolderRef = ref(storage, `admin/${adminId}/folders/${folderId}`);

  // List all files in the source folder recursively
  async function copyFilesRecursively(sourceRef, targetRef) {
    const listResult = await listAll(sourceRef);

    // Copy files
    for (const itemRef of listResult.items) {
      const fileUrl = await getDownloadURL(itemRef);
      const fileName = itemRef.name;
      const targetFileRef = ref(targetRef, fileName);

      // Fetch the file as blob
      const response = await fetch(fileUrl);
      const blob = await response.blob();

      // Upload to target location
      await uploadBytes(targetFileRef, blob);
    }

    // Recursively copy subfolders
    for (const prefixRef of listResult.prefixes) {
      const newTargetRef = ref(targetRef, prefixRef.name);
      await copyFilesRecursively(prefixRef, newTargetRef);
    }
  }

  await copyFilesRecursively(sourceFolderRef, targetFolderRef);
}

/**
 * Logs an action in the audit logs collection.
 * @param {string} action - e.g. "approve", "reject"
 * @param {string} userId
 * @param {string} folderId
 * @param {string} adminId
 * @param {string} notes
 */
async function logAuditAction(action, userId, folderId, adminId, notes) {
  const auditLogsRef = collection(db, "admin", "auditLogs", "logs");
  await addDoc(auditLogsRef, {
    action,
    userId,
    folderId,
    adminId,
    notes,
    timestamp: serverTimestamp(),
  });
}

/**
 * Sends a notification to the user by updating a notifications collection or folder document.
 * @param {string} userId
 * @param {string} folderId
 * @param {string} status - "approved" or "rejected"
 * @param {string} notes
 */

/**
 * Recursively copies a Firestore document and all its subcollections from source to target.
 * This version uses a workaround to list subcollections by querying known collection names.
 * @param {DocumentReference} sourceDocRef
 * @param {DocumentReference} targetDocRef
 */
export async function copyFirestoreDocWithSubcollections(
  sourceDocRef,
  targetDocRef,
  userId,
  adminId
) {
  const sourceDocSnap = await getDoc(sourceDocRef);
  if (!sourceDocSnap.exists()) {
    throw new Error("Source document does not exist");
  }
  const data = sourceDocSnap.data();

  // Update or add storagePath in the document data
  const userPrefix = `users/${userId}/folders/`;
  const adminPrefix = `admin/${adminId}/folders/`;

  // Logging what we're working with
  console.log(`\n[PROCESSING] Document at ${sourceDocRef.path}`);
  console.log("storagePath:", data.storagePath);

  if (data.storagePath && typeof data.storagePath === "string") {
    const regex = new RegExp(userPrefix, "g");
    const testResult = regex.test(data.storagePath);

    console.log("Regex for matching:", regex);
    console.log("Test result:", testResult);

    if (testResult) {
      console.log("[MATCH] Attempting to replace storagePath...");
      const newPath = data.storagePath.replace(regex, adminPrefix);
      console.log(
        `[SUCCESS] Replaced:\n  Old: ${data.storagePath}\n  New: ${newPath}`
      );
      data.storagePath = newPath;
    } else {
      console.log(
        `[NO MATCH] Regex did not match storagePath.\n  Path: "${data.storagePath}"\n  Regex: ${regex}`
      );
    }
  } else if (data.fileName && data.folderId) {
    // Fallback construction
    data.storagePath = `${adminPrefix}${data.folderId}/${data.fileName}`;
    console.log("[FALLBACK] storagePath constructed:", data.storagePath);
  } else {
    console.log("[SKIP] No storagePath to process or construct.");
  }

  // Set the target document with the updated data
  await setDoc(targetDocRef, data);

  // Known subcollections to recursively copy
  const knownSubcollections = ["subfolders", "files"]; // update as needed

  for (const subcollectionName of knownSubcollections) {
    const sourceSubcollectionRef = collection(
      sourceDocRef.firestore,
      sourceDocRef.path,
      subcollectionName
    );
    const targetSubcollectionRef = collection(
      targetDocRef.firestore,
      targetDocRef.path,
      subcollectionName
    );

    const querySnapshot = await getDocs(sourceSubcollectionRef);
    for (const docSnap of querySnapshot.docs) {
      const sourceSubDocRef = doc(sourceSubcollectionRef, docSnap.id);
      const targetSubDocRef = doc(targetSubcollectionRef, docSnap.id);

      // Recursively copy subdocuments
      await copyFirestoreDocWithSubcollections(
        sourceSubDocRef,
        targetSubDocRef,
        userId,
        adminId
      );
    }
  }
}
/**
 * Approves a submission.
 * @param {string} userId
 * @param {string} folderId
 * @param {string} adminNotes
 * @param {string} adminId
 */
export async function approveSubmission(userId, folderId, adminNotes, adminId) {
  // Copy files in storage with adminId
  console.log("ApprovedSubmissions");
  await copyFolderFiles(userId, folderId, adminId);

  // Update admin collection with approved folder metadata using adminId path
  const adminFolderDocRef = doc(
    db,
    "users",
    adminId,
    "approved_folders",
    folderId
  );
  const userFolderDocRef = doc(db, "users", userId, "folders", folderId);

  //Getting the full name of the admin
  const adminName = await getAdminFullName(adminId);
  console.log("Admin Name: ", adminName);
  console.log("Folder ID: ", folderId);
  console.log("User ID: ", userId);
  //Getting the admin avatar path
  const avatarPath = `admin/${adminId}/avatar`;
  const adminAvatar = await getOnlyAvatarFilePath(avatarPath);

  // Recursively copy folder document and all subcollections
  await copyFirestoreDocWithSubcollections(
    userFolderDocRef,
    adminFolderDocRef,
    userId,
    adminId
  );

  // Update admin folder document with approval info
  await updateDoc(adminFolderDocRef, {
    status: "approved",
    adminNotes,
    approvedAt: serverTimestamp(),
    approvedBy: adminName,
    adminAvatarPath: adminAvatar,
  });

  await updateDoc(userFolderDocRef, {
    status: "approved",
    adminNotes,
    approvedAt: serverTimestamp(),
    approvedBy: adminName,
    adminAvatarPath: adminAvatar,
  });

  // Remove submission from pending list (assuming a submissions collection)
  const submissionsQuery = query(
    collection(db, "submissions"),
    where("userId", "==", userId),
    where("folderId", "==", folderId)
  );
  const submissionsSnapshot = await getDocs(submissionsQuery);
  for (const docSnap of submissionsSnapshot.docs) {
    await deleteDoc(docSnap.ref);
  }

  // Log audit action
  await logAuditAction("approve", userId, folderId, adminId, adminNotes);
}

/**
 * Rejects a submission.
 * @param {string} userId
 * @param {string} folderId
 * @param {string} adminNotes
 * @param {string} adminId
 */
export async function rejectSubmission(userId, folderId, adminNotes, adminId) {
  const userFolderDocRef = doc(db, "users", userId, "folders", folderId);

  const adminName = await getAdminFullName(adminId);

  const avatarPath = `admin/${adminId}/avatar`;
  const adminAvatar = await getOnlyAvatarFilePath(avatarPath);
  // Update submission and user folder status with rejection and notes
  await updateDoc(userFolderDocRef, {
    status: "rejected",
    adminNotes,
    rejectedAt: serverTimestamp(),
    rejectedBy: adminName,
    adminAvatarPath: adminAvatar,
  });

  // Optionally keep files intact (no file operations here)

  // Remove submission from pending list (assuming a submissions collection)
  const submissionsQuery = query(
    collection(db, "submissions"),
    where("userId", "==", userId),
    where("folderId", "==", folderId)
  );
  const submissionsSnapshot = await getDocs(submissionsQuery);
  for (const docSnap of submissionsSnapshot.docs) {
    await deleteDoc(docSnap.ref);
  }

  // Log audit action
  await logAuditAction("reject", userId, folderId, adminId, adminNotes);
}

export async function getAdminFullName(adminId) {
  try {
    const adminDocRef = doc(db, "users", adminId);
    const adminDocSnap = await getDoc(adminDocRef);
    if (adminDocSnap.exists()) {
      return adminDocSnap.data().fullName || "";
    }
    return "";
  } catch (error) {
    console.error("Error fetching admin fullName:", error);
    return "";
  }
}

export async function getOnlyAvatarFilePath(folderPath) {
  try {
    const folderRef = ref(storage, folderPath);
    const res = await listAll(folderRef);
    if (res.items.length === 1) {
      // Exactly one avatar in folder
      return res.items[0].fullPath;
    } else if (res.items.length > 1) {
      // Multiple files found, warn about ambiguity
      console.warn(
        "More than one avatar found in folder, using the first:",
        res.items[0].name
      );
      return res.items[0].fullPath;
    } else {
      // No files
      return null;
    }
  } catch (error) {
    console.error("Error retrieving avatar file from folder:", error);
    return null;
  }
}
